import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { PROMO_TYPES, promoBadge } from "@/lib/promotions";
import { Tag, Plus, Eye, MousePointerClick } from "lucide-react";

const BLANK = {
  title: "",
  promo_type: "special_buy",
  badge_label: "",
  description: "",
  terms: "",
  start_date: "",
  end_date: "",
  logo_url: "",
  display_format: "text",
  linked_vehicle_id: "",
};

const STATUS = {
  pending: { label: "Pending review", cls: "bg-amber-50 text-amber-700" },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700" },
  rejected: { label: "Not approved", cls: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400" },
};

export default function PartnerSpecials() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [vehicles, setVehicles] = useState([]);

  const load = () => api.get("/promotions/mine").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/partner/vehicles").then(({ data }) => setVehicles(data || [])).catch(() => setVehicles([]));
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Give your special a title"); return; }
    if (form.display_format === "banner" && !form.linked_vehicle_id) {
      toast.error("Choose the vehicle this banner promotes");
      return;
    }
    if (form.display_format === "banner" && (!form.start_date || !form.end_date)) {
      toast.error("Banner specials need a start and end date");
      return;
    }
    try { await api.post("/promotions/submit", form); toast.success("Submitted for broker approval"); setOpen(false); setForm(BLANK); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't submit"); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-rose-50 text-rose-600"><Tag className="h-5 w-5" strokeWidth={2.5} /></div>
            <div>
              <h1 className="font-display text-3xl font-black tracking-tighter text-slate-950 dark:text-slate-50">My specials</h1>
              <p className="text-sm text-slate-600 dark:text-slate-300">Submit dealer offers. A broker reviews each one before it goes live on your inventory.</p>
            </div>
          </div>
          <Button onClick={() => { setForm(BLANK); setOpen(true); }} data-testid="partner-promo-new" className="rounded-md bg-rose-600 font-bold text-white hover:bg-rose-700"><Plus className="mr-1.5 h-4 w-4" /> Submit a special</Button>
        </div>

        {items === null ? (
          <p className="mt-10 text-slate-400">Loading…</p>
        ) : items.length === 0 ? (
          <div className="mt-10 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 py-16 text-center" data-testid="partner-promo-empty">
            <Tag className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-semibold text-slate-500 dark:text-slate-400">No specials submitted yet</p>
          </div>
        ) : (
          <div className="mt-8 space-y-3" data-testid="partner-promo-list">
            {items.map((p) => {
              const st = STATUS[p.status] || STATUS.pending;
              return (
                <div key={p.id} data-testid={`partner-promo-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-bold ${PROMO_TYPES[p.promo_type]?.cls || "bg-rose-600 text-white"}`}>{promoBadge(p)}</span>
                      <span className="font-display text-base font-bold text-slate-900 dark:text-slate-100">{p.title}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${st.cls}`}>{st.label}</span>
                    </div>
                    {p.description && <p className="mt-1 truncate text-sm text-slate-500 dark:text-slate-400">{p.description}</p>}
                  </div>
                  <div className="flex shrink-0 items-center gap-3 text-xs font-semibold text-slate-500 dark:text-slate-400">
                    <span className="inline-flex items-center gap-1" data-testid={`partner-promo-views-${p.id}`}><Eye className="h-3.5 w-3.5" /> {p.views || 0}</span>
                    <span className="inline-flex items-center gap-1"><MousePointerClick className="h-3.5 w-3.5" /> {p.clicks || 0}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="partner-promo-dialog" className="max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Submit a special</AlertDialogTitle>
            <AlertDialogDescription>This offer applies to your dealership's inventory and goes live once a broker approves it.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Title *</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="partner-promo-title" className="mt-1.5" placeholder="e.g. Year-end clearance" /></div>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Presentation</Label>
              <Select value={form.display_format} onValueChange={(v) => set("display_format", v)}>
                <SelectTrigger className="mt-1.5" data-testid="partner-promo-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text offer</SelectItem>
                  <SelectItem value="banner">3:1 banner special</SelectItem>
                </SelectContent>
              </Select>
              {form.display_format === "banner" && (
                <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                  Submit the offer details below. Your broker adds the banner artwork before publishing.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Type</Label>
              <Select value={form.promo_type} onValueChange={(v) => set("promo_type", v)}><SelectTrigger className="mt-1.5" data-testid="partner-promo-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PROMO_TYPES).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Badge label (optional)</Label><Input value={form.badge_label} onChange={(e) => set("badge_label", e.target.value)} data-testid="partner-promo-badge" className="mt-1.5" /></div>
            <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Logo URL (optional)</Label><Input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} data-testid="partner-promo-logo" className="mt-1.5" placeholder="Paste a logo image URL" /></div>
            <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="partner-promo-desc" className="mt-1.5" /></div>
            <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Terms (optional)</Label><Input value={form.terms} onChange={(e) => set("terms", e.target.value)} data-testid="partner-promo-terms" className="mt-1.5" /></div>
            {form.display_format === "banner" && (
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Linked vehicle *</Label>
                <Select value={form.linked_vehicle_id} onValueChange={(v) => set("linked_vehicle_id", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="partner-banner-vehicle">
                    <SelectValue placeholder="Choose an active vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim || ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Start date</Label><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="partner-promo-start" className="mt-1.5" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">End date</Label><Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="partner-promo-end" className="mt-1.5" /></div>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="partner-promo-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="partner-promo-submit" onClick={submit} className="bg-rose-600 text-white hover:bg-rose-700">Submit for approval</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
